import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  clearCatalogAdminOverride,
  getCatalogAdminPart,
  saveCatalogAdminOverride,
  type CatalogAdminPartDetail,
  type CompatibilityProfile,
  type WatchPart,
} from '../api/client'
import { compactSpecs, formatPrice } from '../lib/partDisplay'
import { providerDisplayName } from '../outbound/links'
import { CatalogCompatibilityFields } from './CatalogCompatibilityFields'
import { CatalogCaseVariantsPanel } from './CatalogCaseVariantsPanel'
import {
  CatalogComplicationEditor,
  parseCaseGeometry,
  parseDialLayout,
  parseMovementCapabilities,
  type CaseGeometry,
  type DialLayout,
  type MovementCapabilities,
} from './CatalogComplicationEditor'
import { JsonViewer } from './JsonViewer'

const PART_TYPES = [
  'case',
  'dial',
  'hands',
  'movement',
  'strap',
  'bezel',
  'crystal',
  'crown',
]

const MATERIALS = [
  '',
  'stainless_steel',
  'titanium',
  'gold',
  'ceramic',
  'leather',
  'rubber',
]

function emptyCompatibility(): CompatibilityProfile {
  return {
    movement_id: null,
    compatible_movement_ids: [],
    case_diameter: null,
    lug_width: null,
    dial_diameter: null,
    water_resistance: null,
    hand_hole_sizes: null,
    case_models: [],
    crown_diameter: null,
    crown_thickness: null,
    stem_type: null,
    crown_included: null,
  }
}

function buildCompatibilityPatch(compatibility: CompatibilityProfile): Record<string, unknown> {
  return {
    compatibility: {
      movement_id: compatibility.movement_id,
      compatible_movement_ids: compatibility.compatible_movement_ids,
      case_diameter: compatibility.case_diameter,
      lug_width: compatibility.lug_width,
      dial_diameter: compatibility.dial_diameter,
      water_resistance: compatibility.water_resistance,
      hand_hole_sizes: compatibility.hand_hole_sizes,
      case_models: compatibility.case_models,
      crown_diameter: compatibility.crown_diameter,
      crown_thickness: compatibility.crown_thickness,
      stem_type: compatibility.stem_type,
      crown_included: compatibility.crown_included,
    },
  }
}

const COMPLICATION_PART_TYPES = new Set(['dial', 'movement', 'case'])

export function CatalogPartEditor() {
  const { partId: encodedPartId } = useParams()
  const partId = encodedPartId ? decodeURIComponent(encodedPartId) : ''

  const [detail, setDetail] = useState<CatalogAdminPartDetail | null>(null)
  const [patch, setPatch] = useState<Record<string, unknown>>({})
  const [note, setNote] = useState('')
  const [compatibility, setCompatibility] = useState<CompatibilityProfile>(emptyCompatibility())
  const [partType, setPartType] = useState('case')
  const [material, setMaterial] = useState('')
  const [available, setAvailable] = useState(true)
  const [dialLayout, setDialLayout] = useState<DialLayout>({ openings: [], required_movement_ids: [] })
  const [movementCapabilities, setMovementCapabilities] = useState<MovementCapabilities>({
    displays: [],
    movement_type: null,
    crown_position: null,
    pusher_positions: [],
  })
  const [caseGeometry, setCaseGeometry] = useState<CaseGeometry>({
    crown_position: null,
    pusher_positions: [],
    case_style: null,
  })
  const [advancedPatchJson, setAdvancedPatchJson] = useState('{}')
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  const loadPart = useCallback(async () => {
    if (!partId) return
    setIsLoading(true)
    setError(null)
    try {
      const loaded = await getCatalogAdminPart(partId)
      setDetail(loaded)
      setPatch(loaded.patch)
      setNote(loaded.note ?? '')
      setCompatibility({ ...loaded.part.compatibility })
      setPartType(loaded.part.type)
      setMaterial(loaded.part.material ?? '')
      setAvailable(
        typeof loaded.part.metadata.available === 'boolean'
          ? loaded.part.metadata.available
          : true,
      )
      setDialLayout(parseDialLayout(loaded.part.metadata.dial_layout))
      setMovementCapabilities(parseMovementCapabilities(loaded.part.metadata.movement_capabilities))
      setCaseGeometry(parseCaseGeometry(loaded.part.metadata.case_geometry))
      setAdvancedPatchJson(JSON.stringify(loaded.patch, null, 2))
      setDirty(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load part')
    } finally {
      setIsLoading(false)
    }
  }, [partId])

  useEffect(() => {
    void loadPart()
  }, [loadPart])

  const overriddenPaths = detail?.overridden_paths ?? []

  const mergedPatch = useMemo(() => {
    const metadataPatch: Record<string, unknown> = {
      ...((patch.metadata as Record<string, unknown>) ?? {}),
      available,
    }
    if (COMPLICATION_PART_TYPES.has(partType)) {
      if (partType === 'dial') metadataPatch.dial_layout = dialLayout
      if (partType === 'movement') metadataPatch.movement_capabilities = movementCapabilities
      if (partType === 'case') metadataPatch.case_geometry = caseGeometry
    }

    let next: Record<string, unknown> = {
      ...buildCompatibilityPatch(compatibility),
      type: partType,
      metadata: metadataPatch,
    }

    if (material) {
      next.material = material
    } else {
      next.material = null
    }

    return next
  }, [patch, compatibility, partType, material, available, dialLayout, movementCapabilities, caseGeometry])

  const markDirty = () => setDirty(true)

  const buildPatchFromForms = (): Record<string, unknown> => {
    const metadataPatch: Record<string, unknown> = {
      ...((patch.metadata as Record<string, unknown>) ?? {}),
      available,
    }

    if (partType === 'dial') {
      metadataPatch.dial_layout = dialLayout
    }
    if (partType === 'movement') {
      metadataPatch.movement_capabilities = movementCapabilities
    }
    if (partType === 'case') {
      metadataPatch.case_geometry = caseGeometry
    }

    let next: Record<string, unknown> = {
      ...buildCompatibilityPatch(compatibility),
      type: partType,
      metadata: metadataPatch,
    }

    if (material) {
      next.material = material
    } else {
      next.material = null
    }

    return next
  }

  const handleSave = async () => {
    if (!partId) return
    setIsSaving(true)
    setError(null)
    setSaveMessage(null)
    try {
      const nextPatch = buildPatchFromForms()
      const saved = await saveCatalogAdminOverride(partId, {
        patch: nextPatch,
        note: note.trim() || null,
      })
      setDetail(saved)
      setPatch(saved.patch)
      setAdvancedPatchJson(JSON.stringify(saved.patch, null, 2))
      setDirty(false)
      setSaveMessage('Saved. Overrides will apply on the next catalog re-ingest.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClearOverrides = async () => {
    if (!partId || !window.confirm('Clear all overrides for this part?')) return
    setIsSaving(true)
    setError(null)
    try {
      const cleared = await clearCatalogAdminOverride(partId)
      setDetail(cleared)
      setPatch({})
      setNote('')
      setAdvancedPatchJson('{}')
      setDirty(false)
      setSaveMessage('Overrides cleared. Re-ingest to refresh catalog values fully.')
      await loadPart()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Clear failed')
    } finally {
      setIsSaving(false)
    }
  }

  const resetSection = (section: 'compatibility' | 'complications' | 'general') => {
    if (!detail) return
    const next = { ...patch }
    if (section === 'compatibility') {
      delete next.compatibility
      setCompatibility({ ...detail.part.compatibility })
    }
    if (section === 'general') {
      delete next.type
      delete next.material
      const metadata = { ...((next.metadata as Record<string, unknown>) ?? {}) }
      delete metadata.available
      if (Object.keys(metadata).length) {
        next.metadata = metadata
      } else {
        delete next.metadata
      }
      setPartType(detail.part.type)
      setMaterial(detail.part.material ?? '')
      setAvailable(
        typeof detail.part.metadata.available === 'boolean'
          ? detail.part.metadata.available
          : true,
      )
    }
    if (section === 'complications') {
      const metadata = { ...((next.metadata as Record<string, unknown>) ?? {}) }
      delete metadata.dial_layout
      delete metadata.movement_capabilities
      delete metadata.case_geometry
      if (Object.keys(metadata).length) {
        next.metadata = metadata
      } else {
        delete next.metadata
      }
      setDialLayout(parseDialLayout(detail.part.metadata.dial_layout))
      setMovementCapabilities(parseMovementCapabilities(detail.part.metadata.movement_capabilities))
      setCaseGeometry(parseCaseGeometry(detail.part.metadata.case_geometry))
    }
    setPatch(next)
    setAdvancedPatchJson(JSON.stringify(next, null, 2))
    markDirty()
  }

  const isOverridden = (path: string) => overriddenPaths.includes(path)

  if (!partId) {
    return <p className="form-error">Missing part id.</p>
  }

  if (isLoading) {
    return <div className="debug-page catalog-admin-page">Loading part…</div>
  }

  if (!detail) {
    return (
      <div className="debug-page catalog-admin-page">
        <p className="form-error">{error ?? 'Part not found.'}</p>
        <Link to="/admin/catalog" className="btn-secondary">
          Back to search
        </Link>
      </div>
    )
  }

  const part: WatchPart = detail.part
  const purchaseUrl =
    (part.purchase_url as string | null) ??
    (typeof part.metadata.source_url === 'string' ? part.metadata.source_url : null)
  const providerLabel = providerDisplayName(part.provider_id)
  const specs = compactSpecs(part)
  const showComplications = COMPLICATION_PART_TYPES.has(partType)

  return (
    <div className="debug-page catalog-admin-page catalog-part-editor">
      <div className="catalog-editor-sticky-top">
        <Link to="/admin/catalog" className="btn-secondary">
          ← Catalog
        </Link>
        <div className="catalog-editor-sticky-top-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleSave()}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save overrides'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void handleClearOverrides()}
            disabled={isSaving}
          >
            Clear overrides
          </button>
        </div>
      </div>

      <div className="catalog-editor-layout">
        <aside className="catalog-editor-hero">
          <div className="catalog-editor-hero-image">
            {part.image_url ? (
              <img src={part.image_url} alt={part.name} />
            ) : (
              <div className="catalog-editor-hero-placeholder">
                {part.type.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="catalog-editor-hero-facts">
            <div><strong>{formatPrice(part.price, part.currency)}</strong></div>
            <div>{providerLabel}</div>
            {purchaseUrl ? (
              <a href={purchaseUrl} target="_blank" rel="noreferrer" className="catalog-source-link">
                View listing
              </a>
            ) : null}
          </div>
          {specs.length > 0 ? (
            <div className="catalog-editor-hero-specs">
              <dl>
                {specs.map(([label, value]) => (
                  <div key={label} className="spec-row">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </aside>

        <div className="catalog-editor-main">
          <header className="catalog-editor-header">
            <h1>{part.name}</h1>
            <p className="debug-subtitle monospace-code">{part.id}</p>
          </header>

          <div className="catalog-editor-meta">
            <label>
              Note
              <input
                type="text"
                value={note}
                onChange={(event) => {
                  setNote(event.target.value)
                  markDirty()
                }}
                placeholder="Why this override exists"
              />
            </label>
          </div>

          <section className="catalog-editor-section">
            <div className="catalog-section-actions">
              <button
                type="button"
                className="btn-secondary btn-small"
                onClick={() => resetSection('compatibility')}
              >
                Reset to catalog
              </button>
            </div>
            <h2>Compatibility</h2>
            <CatalogCompatibilityFields
              partType={partType}
              compatibility={compatibility}
              overriddenPaths={overriddenPaths}
              onChange={(next) => {
                setCompatibility(next)
                markDirty()
              }}
            />
          </section>

          {partType === 'case' ? <CatalogCaseVariantsPanel part={part} /> : null}

          {showComplications ? (
            <section className="catalog-editor-section">
              <div className="catalog-section-actions">
                <button
                  type="button"
                  className="btn-secondary btn-small"
                  onClick={() => resetSection('complications')}
                >
                  Reset to catalog
                </button>
              </div>
              <h2>Complications</h2>
              <CatalogComplicationEditor
                partType={partType}
                dialLayout={dialLayout}
                movementCapabilities={movementCapabilities}
                caseGeometry={caseGeometry}
                onDialLayoutChange={(layout) => {
                  setDialLayout(layout)
                  markDirty()
                }}
                onMovementCapabilitiesChange={(caps) => {
                  setMovementCapabilities(caps)
                  markDirty()
                }}
                onCaseGeometryChange={(geometry) => {
                  setCaseGeometry(geometry)
                  markDirty()
                }}
              />
            </section>
          ) : null}

          <section className="catalog-editor-section">
            <div className="catalog-section-actions">
              <button
                type="button"
                className="btn-secondary btn-small"
                onClick={() => resetSection('general')}
              >
                Reset to catalog
              </button>
            </div>
            <h2>General</h2>
            <div className="catalog-form-grid">
              <label className={isOverridden('type') ? 'field-overridden' : ''}>
                Part type
                <select
                  value={partType}
                  onChange={(event) => {
                    setPartType(event.target.value)
                    markDirty()
                  }}
                >
                  {PART_TYPES.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className={isOverridden('material') ? 'field-overridden' : ''}>
                Material
                <select
                  value={material}
                  onChange={(event) => {
                    setMaterial(event.target.value)
                    markDirty()
                  }}
                >
                  {MATERIALS.map((value) => (
                    <option key={value || 'none'} value={value}>
                      {value ? value.replace('_', ' ') : 'None'}
                    </option>
                  ))}
                </select>
              </label>
              <label className={isOverridden('metadata.available') ? 'field-overridden' : ''}>
                <span className="catalog-checkbox">
                  <input
                    type="checkbox"
                    checked={available}
                    onChange={(event) => {
                      setAvailable(event.target.checked)
                      markDirty()
                    }}
                  />
                  Available in catalog
                </span>
              </label>
            </div>
            <p className="catalog-hint">
              Name, price, and description are read-only (sourced from merchant listings).
            </p>
          </section>

          <div className="catalog-accordion">
            <button
              type="button"
              className="catalog-accordion-trigger"
              onClick={() => setAdvancedOpen((open) => !open)}
              aria-expanded={advancedOpen}
            >
              Advanced JSON
              <span>{advancedOpen ? '−' : '+'}</span>
            </button>
            {advancedOpen ? (
              <div className="catalog-accordion-content">
                <label>
                  Override patch (JSON)
                  <textarea
                    rows={12}
                    className="monospace-code"
                    value={advancedPatchJson}
                    onChange={(event) => {
                      setAdvancedPatchJson(event.target.value)
                      markDirty()
                    }}
                  />
                </label>
                <p className="catalog-hint">
                  View or edit the raw patch JSON. Saving always uses the form fields above.
                </p>
                <JsonViewer title="Effective part" data={part} />
                <JsonViewer title="Override patch" data={patch} />
                <JsonViewer title="Patch from forms (unsaved)" data={mergedPatch} />
              </div>
            ) : null}
          </div>

          {error ? <p className="form-error">{error}</p> : null}
          {saveMessage ? <p className="catalog-save-message">{saveMessage}</p> : null}
          {dirty ? <p className="catalog-hint">You have unsaved changes.</p> : null}
        </div>
      </div>

      <div className="catalog-editor-sticky-bottom">
        <Link to="/admin/catalog" className="btn-secondary">
          Back
        </Link>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void handleSave()}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save overrides'}
        </button>
      </div>
    </div>
  )
}
